import {ScratchStorage} from 'scratch-storage';

import defaultProject from './default-project';
import {getLibraryAssetURL, isLibraryAsset} from './libraries/library-assets';
import shouldUseWindowFetch from './storage-fetch';

/**
 * Wrapper for ScratchStorage which adds default web sources.
 * @todo make this more configurable
 */
class Storage extends ScratchStorage {
    constructor () {
        super();
        // Hydro serves library assets itself. Use the regular window fetch
        // implementation for every Hydro editor, not just the embedded exam
        // editor. The worker fetch tool can otherwise stay pending and leave
        // sprite/backdrop selection spinning forever. Window fetch correctly
        // settles the VM asset-loading promises for same- and cross-origin
        // Hydro editor deployments.
        if (shouldUseWindowFetch(typeof window === 'undefined' ? null : window)) {
            this.webHelper.assetTool.tools = this.webHelper.assetTool.tools.slice(-1);
        }
        this.cacheDefaultProject();
    }
    addOfficialScratchWebStores () {
        this.addWebStore(
            [this.AssetType.Project],
            this.getProjectGetConfig.bind(this),
            this.getProjectCreateConfig.bind(this),
            this.getProjectUpdateConfig.bind(this)
        );
        this.addWebStore(
            [this.AssetType.ImageVector, this.AssetType.ImageBitmap, this.AssetType.Sound],
            this.getAssetGetConfig.bind(this),
            // We set both the create and update configs to the same method because
            // storage assumes it should update if there is an assetId, but the
            // asset store uses the assetId as part of the create URI.
            this.getAssetCreateConfig.bind(this),
            this.getAssetCreateConfig.bind(this)
        );
        this.addWebStore(
            [this.AssetType.Sound],
            asset => new URL(
                window.__EXAM_SCRATCH_CONFIG__?.examMode === '1' ?
                    `/exam-scratch-static/extension-assets/scratch3_music/${asset.assetId}.${asset.dataFormat}` :
                    `static/extension-assets/scratch3_music/${asset.assetId}.${asset.dataFormat}`,
                window.__EXAM_SCRATCH_CONFIG__?.examMode === '1' ?
                    window.location.origin :
                    (window.__EXAM_SCRATCH_CONFIG__?.scratchEditorUrl || window.location.href)
            ).href
        );
    }
    setProjectHost (projectHost) {
        this.projectHost = projectHost;
    }
    setProjectToken (projectToken) {
        this.projectToken = projectToken;
    }
    getProjectGetConfig (projectAsset) {
        const path = `${this.projectHost}/${projectAsset.assetId}`;
        const qs = this.projectToken ? `?token=${this.projectToken}` : '';
        return path + qs;
    }
    getProjectCreateConfig () {
        return {
            url: `${this.projectHost}/`,
            withCredentials: true
        };
    }
    getProjectUpdateConfig (projectAsset) {
        return {
            url: `${this.projectHost}/${projectAsset.assetId}`,
            withCredentials: true
        };
    }
    setAssetHost (assetHost) {
        this.assetHost = assetHost;
    }
    getAssetGetConfig (asset) {
        if (isLibraryAsset(asset)) {
            return getLibraryAssetURL(`${asset.assetId}.${asset.dataFormat}`);
        }
        return `${this.assetHost}/internalapi/asset/${asset.assetId}.${asset.dataFormat}/get/`;
    }
    getAssetCreateConfig (asset) {
        return {
            // There is no such thing as updating assets, but storage assumes it
            // should update if there is an assetId, and the asset store uses the
            // assetId as part of the create URI. So, force the method to POST.
            // Then when storage finds this config to use for the "update", still POSTs
            method: 'post',
            url: `${this.assetHost}/${asset.assetId}.${asset.dataFormat}`,
            withCredentials: true
        };
    }
    setTranslatorFunction (translator) {
        this.translator = translator;
        this.cacheDefaultProject();
    }
    cacheDefaultProject () {
        const defaultProjectAssets = defaultProject(this.translator);
        defaultProjectAssets.forEach(asset => this.builtinHelper._store(
            this.AssetType[asset.assetType],
            this.DataFormat[asset.dataFormat],
            asset.data,
            asset.id
        ));
    }
}

const storage = new Storage();

export default storage;
